import { useState, useEffect, useCallback } from "react";

const ADMIN_TOKEN_KEY = "cheogram_admin_token";
const BASE = "";

interface Metrics {
  requests: { totalRequests: number; avgDurationMs: number; statusCodes: Record<string, number> };
  websocket: { activeClients: number; totalEvents: number };
  push: { totalSubscriptions: number; notificationsSent: number };
  database: { totalMessages: number; totalChats: number };
  runtime: {
    goVersion: string; arch: string; cpus: number; goroutines: number;
    heapAllocMB: number; stackMB: number; gcCycles: number;
  };
  uptime: string;
}

interface Session { jid: string; boundJid: string; connected: boolean; rosterSize: number }
interface AuditEntry { timestamp: string; actor: string; action: string; detail: string }
interface HealthCheck { healthy: boolean; status: string; checks: Record<string, { healthy: boolean; status: string }> }

async function adminFetch<T>(path: string): Promise<T> {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY) || "";
  const res = await fetch(`${BASE}${path}`, { headers: { "X-Admin-Token": token } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [sessions, setSessions] = useState<{ sessions: Session[]; total: number } | null>(null);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [audit, setAudit] = useState<{ entries: AuditEntry[] } | null>(null);
  const [tab, setTab] = useState<"overview" | "sessions" | "audit">("overview");

  const authenticate = async () => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    try {
      await adminFetch("/api/admin/health/deep");
      setAuthed(true);
      setError("");
    } catch {
      setError("Invalid token");
      setAuthed(false);
    }
  };

  const refresh = useCallback(async () => {
    if (!authed) return;
    try {
      const [m, s, h, a] = await Promise.all([
        adminFetch<Metrics>("/api/admin/metrics"),
        adminFetch<{ sessions: Session[]; total: number }>("/api/admin/sessions"),
        adminFetch<HealthCheck>("/api/admin/health/deep"),
        adminFetch<{ entries: AuditEntry[] }>("/api/admin/audit"),
      ]);
      setMetrics(m);
      setSessions(s);
      setHealth(h);
      setAudit(a);
    } catch {
      // ignore
    }
  }, [authed]);

  useEffect(() => {
    if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
      adminFetch("/api/admin/health/deep").then(() => setAuthed(true)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!authed) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6">
        <div className="glass-panel rounded-2xl p-6 w-full max-w-sm">
          <h1 className="text-lg font-semibold text-white mb-4">Admin Login</h1>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && authenticate()}
            placeholder="Admin token"
            className="w-full px-4 py-2.5 rounded-xl bg-white/8 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
          <button
            onClick={authenticate}
            className="w-full mt-4 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-light transition-colors"
          >
            Authenticate
          </button>
        </div>
      </div>
    );
  }

  const r = metrics?.runtime;
  const healthColor = health?.healthy ? "bg-green-500" : "bg-red-500";
  const statusCodes = metrics?.requests?.statusCodes || {};

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 pt-4 pb-2">
        {(["overview", "sessions", "audit"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              tab === t ? "bg-primary text-white" : "text-white/50 hover:text-white hover:bg-white/8"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${healthColor}`} />
          <span className="text-xs text-white/40">{health?.status || "..."}</span>
        </div>
      </div>

      {tab === "overview" && (
        <div className="p-4 space-y-4">
          {/* Metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Uptime" value={metrics?.uptime?.replace(/\.\d+s$/, "s") || "..."} />
            <Card label="Goroutines" value={r?.goroutines?.toString() || "..."} />
            <Card label="Heap" value={r ? `${r.heapAllocMB.toFixed(1)} MB` : "..."} />
            <Card label="Sessions" value={sessions?.total?.toString() || "0"} />
            <Card label="Messages" value={metrics?.database?.totalMessages?.toString() || "0"} />
            <Card label="Chats" value={metrics?.database?.totalChats?.toString() || "0"} />
            <Card label="WS Clients" value={metrics?.websocket?.activeClients?.toString() || "0"} />
            <Card label="Requests" value={metrics?.requests?.totalRequests?.toString() || "0"} />
          </div>

          {/* Runtime info */}
          <div className="glass-panel rounded-xl p-4">
            <h3 className="text-xs font-medium text-white/40 uppercase mb-3">Runtime</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <Row k="Go" v={r?.goVersion || "..."} />
              <Row k="Arch" v={r?.arch || "..."} />
              <Row k="CPUs" v={r?.cpus?.toString() || "..."} />
              <Row k="GC Cycles" v={r?.gcCycles?.toString() || "..."} />
              <Row k="Stack" v={r ? `${r.stackMB.toFixed(1)} MB` : "..."} />
              <Row k="Avg Latency" v={metrics?.requests ? `${metrics.requests.avgDurationMs.toFixed(1)} ms` : "..."} />
            </div>
          </div>

          {/* Status codes */}
          {Object.keys(statusCodes).length > 0 && (
            <div className="glass-panel rounded-xl p-4">
              <h3 className="text-xs font-medium text-white/40 uppercase mb-3">Status Codes</h3>
              <div className="flex gap-3 flex-wrap">
                {Object.entries(statusCodes).map(([code, count]) => (
                  <div key={code} className="flex items-center gap-2">
                    <span className={`text-xs font-mono ${code.startsWith("2") ? "text-green-400" : code.startsWith("4") ? "text-yellow-400" : code.startsWith("5") ? "text-red-400" : "text-white/60"}`}>
                      {code}
                    </span>
                    <span className="text-xs text-white/40">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Health checks */}
          {health?.checks && (
            <div className="glass-panel rounded-xl p-4">
              <h3 className="text-xs font-medium text-white/40 uppercase mb-3">Health Checks</h3>
              <div className="space-y-2">
                {Object.entries(health.checks).map(([name, check]) => (
                  <div key={name} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${(check as any).healthy !== false ? "bg-green-500" : "bg-red-500"}`} />
                    <span className="text-sm text-white/80">{name}</span>
                    <span className="text-xs text-white/30 ml-auto">{(check as any).status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "sessions" && (
        <div className="p-4 space-y-3">
          <h2 className="text-sm font-medium text-white/60">{sessions?.total || 0} sessions</h2>
          {sessions?.sessions?.length === 0 && (
            <p className="text-sm text-white/30">No active XMPP sessions</p>
          )}
          {sessions?.sessions?.map((s) => (
            <div key={s.jid} className="glass-panel rounded-xl p-4 flex items-center gap-3">
              <span className={`w-2.5 h-2.5 rounded-full ${s.connected ? "bg-green-500" : "bg-red-500"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{s.jid}</p>
                {s.boundJid && <p className="text-xs text-white/40 truncate">{s.boundJid}</p>}
              </div>
              <span className="text-xs text-white/30">{s.rosterSize} contacts</span>
            </div>
          ))}
        </div>
      )}

      {tab === "audit" && (
        <div className="p-4 space-y-2">
          <h2 className="text-sm font-medium text-white/60">{audit?.entries?.length || 0} entries</h2>
          {audit?.entries?.slice(0, 50).map((e, i) => (
            <div key={i} className="glass-panel rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-[10px] text-white/30 font-mono shrink-0 w-16">
                {new Date(e.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="text-xs font-medium text-primary shrink-0">{e.actor}</span>
              <span className="text-xs text-white/70">{e.action}</span>
              {e.detail && <span className="text-xs text-white/30 ml-auto truncate max-w-[150px]">{e.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Card = ({ label, value }: { label: string; value: string }) => (
  <div className="glass-panel rounded-xl p-3">
    <p className="text-[10px] text-white/40 uppercase tracking-wider">{label}</p>
    <p className="text-lg font-semibold text-white mt-0.5">{value}</p>
  </div>
);

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex justify-between">
    <span className="text-white/40">{k}</span>
    <span className="text-white font-mono text-xs">{v}</span>
  </div>
);
